import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adminService } from '../../api';
import './AdminAdsPage.css';

// Status is serialized by the API as a number (0-3); name is only used for the
// search-by-name query param, which ASP.NET model binding parses independently of JSON.
const STATUSES = [
    { value: 0, name: 'Draft', label: 'Черновик' },
    { value: 1, name: 'Published', label: 'Опубликовано' },
    { value: 2, name: 'Blocked', label: 'Заблокировано' },
    { value: 3, name: 'Archived', label: 'В архиве' },
];

const AdminAdsPage = () => {
    const [items, setItems] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkStatus, setBulkStatus] = useState(STATUSES[1].value);
    const [bulkBusy, setBulkBusy] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userIdFilter = searchParams.get('userId') || undefined;

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        setSelectedIds(new Set());
        adminService.getAds({ page, pageSize: 20, search: search || undefined, status: status || undefined, userId: userIdFilter })
            .then(({ data }) => {
                setItems(data.items);
                setTotalPages(data.totalPages);
            })
            .catch(() => setError('Не удалось загрузить объявления.'))
            .finally(() => setLoading(false));
    }, [page, search, status, userIdFilter]);

    useEffect(() => { load(); }, [load]);

    const toggleSelected = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedIds(prev => prev.size === items.length ? new Set() : new Set(items.map(a => a.id)));
    };

    const applyBulkStatus = async () => {
        if (selectedIds.size === 0) return;
        setBulkBusy(true);
        try {
            const ids = [...selectedIds];
            await adminService.bulkSetAdStatus(ids, bulkStatus);
            setItems(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: bulkStatus } : a));
            setSelectedIds(new Set());
        } catch {
            alert('Не удалось изменить статус выбранных объявлений.');
        } finally {
            setBulkBusy(false);
        }
    };

    const submitSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const changeStatus = async (id, newStatus) => {
        newStatus = Number(newStatus);
        setBusyId(id);
        try {
            await adminService.setAdStatus(id, newStatus);
            setItems(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
        } catch {
            alert('Не удалось изменить статус.');
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Удалить объявление безвозвратно?')) return;
        setBusyId(id);
        try {
            await adminService.deleteAd(id);
            setItems(prev => prev.filter(a => a.id !== id));
        } catch {
            alert('Не удалось удалить объявление.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="admin-ads">
            <h1 className="admin-ads__title">Объявления</h1>

            {userIdFilter && (
                <div className="admin-ads__user-filter">
                    Показаны объявления пользователя {userIdFilter}
                    <button onClick={() => setSearchParams({})}>Сбросить</button>
                </div>
            )}

            <div className="admin-ads__filters">
                <form className="admin-ads__search" onSubmit={submitSearch}>
                    <input
                        className="admin-ads__search-input"
                        placeholder="Поиск по заголовку..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    <button type="submit" className="admin-ads__search-btn">Найти</button>
                </form>

                <select
                    className="admin-ads__status-select"
                    value={status}
                    onChange={e => { setPage(1); setStatus(e.target.value); }}
                >
                    <option value="">Все статусы</option>
                    {STATUSES.map(s => <option key={s.name} value={s.name}>{s.label}</option>)}
                </select>
            </div>

            {selectedIds.size > 0 && (
                <div className="admin-ads__bulk-bar">
                    <span>Выбрано: {selectedIds.size}</span>
                    <select
                        className="admin-ads__status-select"
                        value={bulkStatus}
                        onChange={e => setBulkStatus(Number(e.target.value))}
                        disabled={bulkBusy}
                    >
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button className="admin-ads__btn" onClick={applyBulkStatus} disabled={bulkBusy}>
                        {bulkBusy ? 'Применяю...' : 'Применить'}
                    </button>
                    <button className="admin-ads__btn" onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>
                        Снять выделение
                    </button>
                </div>
            )}

            {loading && <div className="admin-ads__placeholder">Загрузка...</div>}
            {error && <div className="admin-ads__placeholder">{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="admin-ads__placeholder">Объявлений не найдено.</div>
            )}

            {!loading && !error && items.length > 0 && (
                <div className="admin-ads__table-wrap">
                    <table className="admin-ads__table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === items.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th></th>
                                <th>Заголовок</th>
                                <th>Тип</th>
                                <th>Город</th>
                                <th>Цена</th>
                                <th>Автор</th>
                                <th>Статус</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(a => (
                                <tr key={a.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(a.id)}
                                            onChange={() => toggleSelected(a.id)}
                                        />
                                    </td>
                                    <td>
                                        {a.photoUrl && <img className="admin-ads__thumb" src={a.photoUrl} alt="" />}
                                    </td>
                                    <td>
                                        <Link to={`/ad/${a.id}`} target="_blank" className="admin-ads__link">
                                            {a.title || '(без названия)'}
                                        </Link>
                                    </td>
                                    <td>{a.propertyKind}</td>
                                    <td>{a.city || '—'}</td>
                                    <td>{a.price != null ? `${a.price.toLocaleString('ru-RU')} ₽` : '—'}</td>
                                    <td>
                                        {a.userId ? (
                                            <Link to={`/admin/users/${a.userId}`} className="admin-ads__link">
                                                {a.authorName || a.authorEmail || a.userId}
                                            </Link>
                                        ) : (a.authorName || '—')}
                                    </td>
                                    <td>
                                        <select
                                            className="admin-ads__status-select admin-ads__status-select--row"
                                            value={a.status}
                                            disabled={busyId === a.id}
                                            onChange={e => changeStatus(a.id, e.target.value)}
                                        >
                                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="admin-ads__actions">
                                        <button
                                            className="admin-ads__btn"
                                            disabled={busyId === a.id}
                                            onClick={() => navigate(`/admin/ads/edit/${a.id}`)}
                                        >
                                            Редактировать
                                        </button>
                                        <button
                                            className="admin-ads__btn admin-ads__btn--danger"
                                            disabled={busyId === a.id}
                                            onClick={() => remove(a.id)}
                                        >
                                            Удалить
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="admin-ads__pagination">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
                    <span>{page} / {totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Далее</button>
                </div>
            )}
        </div>
    );
};

export default AdminAdsPage;
